import { converge, identity, map, mergeRight, objOf, pipe, prop } from "ramda";
import { isString } from "../../utils/string.utils";
import { useAuth } from "../Authorization";
import { useMyMutation, useMyQuery } from "../Authorization/AuthProvider";

const getPayload = ({
  filter: {
    languages,
    fields,
    experience: [experienceFrom, experienceTo] = [],
    search,
    prices,
    displayFavorites, // just local state now
  },
  page = {
    pageNumber: 0,
    pageSize: 1000000,
  },
}) => {
  return {
    page,
    languages: languages?.length ? languages : undefined,
    fields: fields?.length ? fields : undefined,
    experienceFrom,
    experienceTo,
    name: search,
    prices: prices?.length ? prices : undefined,
  };
};

export const useCoachesQuery = ({ filter, ...rest } = {}) => {
  const coachesQuery = useMyQuery({
    queryKey: ["coaches", filter],
    fetchDef: {
      method: "POST",
      url: "/api/latest/coaches",
      data: getPayload({ filter }),
    },
    refetchOnWindowFocus: false,
    ...rest,
  });

  return coachesQuery;
};

export const useUserUpcomingSessionsQuery = (rest = {}) => {
  /* [ { "coach": "coach", "firstName": "Mitch", "lastName": "Cleverman", "time": "%s" }, ] */
  return useMyQuery({
    queryKey: ["user-info", "upcoming-sessions"],
    fetchDef: {
      url: `/api/latest/user-info/upcoming-sessions`,
      to: map(
        converge(mergeRight, [
          identity,
          pipe(prop("coach"), objOf("username")),
        ]),
      ),
    },
    ...rest,
  });
};

export const usePickCoach = ({ coach, ...rest }) => {
  const pickCoachMutation = useMyMutation({
    fetchDef: {
      method: "POST",
      url: `/api/latest/user-info/coach`,
      from: (username) => ({
        coach:
          isString(username) || username === null ? username : coach.username,
      }),
    },
    // snackbar: { success: true, error: true },
    invalidate: [
      { queryKey: ["user-info"] },
      { queryKey: ["coaches"] },
      { queryKey: ["coach", coach?.username] },
    ],
    ...rest,
  });

  return {
    mutation: pickCoachMutation,
    onPick: coach ? pickCoachMutation.mutate : undefined,
    pickPending: pickCoachMutation.isPending,
  };
};

export const useYourCoachQuery = (rest = {}) => {
  const username = useAuth().user?.data?.coach;
  const yourCoachQuery = useMyQuery({
    queryKey: ["coaches", username],
    fetchDef: { url: `/api/latest/coaches/${username}` },
    enabled: !!username,
    ...rest,
  });
  return yourCoachQuery;
};
